import React, { Suspense, lazy } from 'react';
import { AppProvider, useApp } from './context/AppContext';

import ErrorBoundary from './components/ErrorBoundary';
import UtilityBar from './components/UtilityBar';
import Header from './components/Header';
import PromoBar from './components/PromoBar';
import Hero from './components/Hero';
import TopGames from './components/TopGames';
import RecentlyViewed from './components/RecentlyViewed';
import TrustBar from './components/TrustBar';
import CategoryRail from './components/CategoryRail';
import ProductGrid from './components/ProductGrid';
import Footer from './components/Footer';

import CartDrawer from './components/CartDrawer';
import AuthDrawer from './components/AuthDrawer';
import AccountDrawer from './components/AccountDrawer';
import SearchOverlay from './components/SearchOverlay';
import LiveChat from './components/LiveChat';
import Toasts from './components/Toasts';
import BackToTop from './components/BackToTop';
import CookieBanner from './components/CookieBanner';
import MobileNav from './components/MobileNav';
import { RouteSkeleton } from './components/Skeleton';

// Route-level code splitting — each page is its own chunk
const ProductDetail = lazy(() => import('./components/ProductDetail'));
const CategoryPage = lazy(() => import('./components/CategoryPage'));
const VipPage = lazy(() => import('./components/VipPage'));
const BlogPage = lazy(() => import('./components/BlogPage'));
const Checkout = lazy(() => import('./components/Checkout'));
const InfoPage = lazy(() => import('./components/InfoPage'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));

const KNOWN_ROUTES = new Set([
  'home', 'product', 'category', 'vip', 'blog', 'checkout', 'info',
]);

function HomePage() {
  return (
    <>
      <Hero />
      <ProductGrid />
      <CategoryRail />
      <TopGames />
      <RecentlyViewed />
      <TrustBar />
    </>
  );
}

function Router() {
  const { route } = useApp();
  if (!KNOWN_ROUTES.has(route.page)) {
    return <NotFoundPage />;
  }
  switch (route.page) {
    case 'product':
      return <ProductDetail productId={route.params.id} />;
    case 'category':
      return <CategoryPage categoryId={route.params.id || 'all'} />;
    case 'vip':
      return <VipPage />;
    case 'blog':
      return <BlogPage />;
    case 'checkout':
      return <Checkout />;
    case 'info':
      return <InfoPage topic={route.params.topic || 'about'} />;
    case 'home':
    default:
      return <HomePage />;
  }
}

function Shell() {
  return (
    <div className="app-shell">
      <a href="#main" className="skip-link">略過導航 / Skip to content</a>
      <UtilityBar />
      <PromoBar />
      <Header />
      <main id="main" className="app-main">
        <Suspense fallback={<RouteSkeleton />}>
          <Router />
        </Suspense>
      </main>
      <Footer />

      <CartDrawer />
      <AuthDrawer />
      <AccountDrawer />
      <SearchOverlay />
      <LiveChat />
      <BackToTop />
      <MobileNav />
      <CookieBanner />
      <Toasts />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <Shell />
      </AppProvider>
    </ErrorBoundary>
  );
}
